import { GQL_CLIENT } from 'src/graphqlClient'
import {
  deriveBucket,
  getTaskKindConfig,
} from '@autoriz/shared/assistant'
import {
  RESCHEDULE_TASK_MUTATION,
  DISMISS_TASK_MUTATION,
  COMPLETE_TASK_MUTATION,
  INSERT_MANUAL_TASK_MUTATION,
} from './assistant.gql'
import type { ManualTaskFormData } from '../schemas/manual-task-form'
import type { Task, TaskBucket } from '../schemas/task'

// Reschedule — writes only `target_time`; the task stays visible and its
// derived bucket recomputes against the new deadline.
export const rescheduleTask = async (args: {
  id: string
  chosenDate: string
}): Promise<void> => {
  await GQL_CLIENT.request(RESCHEDULE_TASK_MUTATION, {
    id: args.id,
    chosenDate: args.chosenDate,
  })
}

// Dismiss is one-way; no reactivate mutation exists.
export const dismissTask = async (id: string): Promise<void> => {
  await GQL_CLIENT.request(DISMISS_TASK_MUTATION, {
    id,
    dismissedAt: new Date().toISOString(),
  })
}

// Complete — manual-task validate only. Auto tasks are completed by the
// engine from `deleteOn` predicates.
export const completeTask = async (id: string): Promise<void> => {
  await GQL_CLIENT.request(COMPLETE_TASK_MUTATION, { id })
}

type InsertedRow = {
  id: string
  project_id: number | null
  kind: string
  source: string
  title: string
  reason: string | null
  meta: string | null
  action_label_override: string | null
  created_at: string
  updated_at: string
  visible_from: string
  target_time: string
  dismissed_at: string | null
  completed_at: string | null
  project: {
    id: number
    project_type: string | null
    primary_signatory_client: { first_name: string | null; last_name: string | null } | null
  } | null
}

const toClientName = (
  c: { first_name: string | null; last_name: string | null } | null | undefined,
): string | null => {
  if (!c) return null
  const last = (c.last_name ?? '').trim()
  if (last.length > 0) return last.toUpperCase()
  const first = (c.first_name ?? '').trim()
  return first.length > 0 ? first : null
}

export const insertManualTask = async (
  input: ManualTaskFormData,
): Promise<Task> => {
  const now = new Date()
  const chosen = new Date(input.chosenDate)
  // FR-019: visible_from = min(now, chosenDate). A future échéance still
  // surfaces immediately; a past échéance keeps the picked date.
  const visibleFrom = chosen.getTime() <= now.getTime() ? chosen.toISOString() : now.toISOString()
  const targetTime = chosen.toISOString()
  const response = (await GQL_CLIENT.request(INSERT_MANUAL_TASK_MUTATION, {
    title: input.title,
    reason: input.reason || null,
    meta: input.meta || null,
    projectId: input.projectId ?? null,
    visibleFrom,
    targetTime,
  })) as { insert_assistant_tasks_one: InsertedRow }
  const row = response.insert_assistant_tasks_one
  const bucket: TaskBucket = deriveBucket(
    new Date(row.target_time),
    now,
    getTaskKindConfig(row.kind).priorityThresholds,
  )
  return {
    id: row.id,
    projectId: row.project_id,
    kind: row.kind,
    source: row.source as Task['source'],
    title: row.title,
    reason: row.reason,
    meta: row.meta,
    actionLabelOverride: row.action_label_override,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    visibleFrom: row.visible_from,
    targetTime: row.target_time,
    dismissedAt: row.dismissed_at,
    completedAt: row.completed_at,
    bucket,
    client: toClientName(row.project?.primary_signatory_client),
    projectType: row.project?.project_type ?? null,
  }
}
